import apiClient from './config';
import { DEFAULT_COORDINATES } from '../config/constants';

/**
 * 로컬스토리지 키 상수
 */
const SERVICE_USER_ID_KEY = 'serviceUserId';

/**
 * 로컬스토리지에서 serviceUserId 조회
 */
const getServiceUserId = (): string | null => {
  try {
    return localStorage.getItem(SERVICE_USER_ID_KEY);
  } catch (error) {
    console.warn('[StayService] Failed to get serviceUserId from localStorage:', error);
    return null;
  }
};

// 숙소 주소 정보 타입 정의
export interface StayAddress {
  id: string;
  address: string;
  detail_address: string;
  lat: number;
  lng: number;
  address_kr?: string;
  detail_address_kr?: string;
}

// 숙소 정보 타입 정의
export interface StayInfo {
  id: string;
  stay_name: string;
  stay_id: string;
  address_id: string;
  created_date: string;
  updated_date: string;
  active?: boolean | null;
  address: StayAddress;
}

/** API 요청 timeout (ms) */
const STAY_API_TIMEOUT = 10000;

/** 최대 재시도 횟수 */
const MAX_RETRIES = 2;

/** 재시도 간 대기 시간 (ms) — 2^attempt * BASE_BACKOFF */
const BASE_BACKOFF = 1000;

/**
 * API 응답이 구조적으로 유효한 object인지만 확인합니다.
 * 필드 타입/값은 검증하지 않습니다 — normalizeStayInfo에서 처리합니다.
 */
const isValidResponse = (data: unknown): data is Record<string, unknown> => {
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    console.warn('[StayService] API response is not a valid object:', typeof data);
    return false;
  }
  return true;
};

/**
 * API 응답 데이터를 안전한 StayInfo 형태로 정규화합니다.
 * - 원본 응답의 모든 필드를 보존 (display_info, note 등 인터페이스 외 필드 포함)
 * - null/undefined → 안전한 기본값
 * - 문자열 숫자 → number
 * - 파싱 불가능한 lat/lng → DEFAULT_COORDINATES
 * - 절대 throw하지 않습니다.
 */
const normalizeStayInfo = (data: Record<string, unknown>): StayInfo => {
  // address 객체 추출 (없거나 비-object면 null)
  const rawAddress = (data.address && typeof data.address === 'object' && !Array.isArray(data.address))
    ? data.address as Record<string, unknown>
    : null;

  if (!rawAddress) {
    console.warn('[StayService] normalizeStayInfo: address field missing or not an object, using defaults');
  }

  // lat/lng 정규화: null/undefined → NaN → DEFAULT_COORDINATES
  const rawLat = rawAddress?.lat;
  const rawLng = rawAddress?.lng;
  const normalizedLat = (rawLat != null) ? Number(rawLat) : NaN;
  const normalizedLng = (rawLng != null) ? Number(rawLng) : NaN;

  // 타입 안전 필드 정규화 값
  const normalizedAddressFields = {
    id: String(rawAddress?.id ?? ''),
    address: String(rawAddress?.address ?? ''),
    detail_address: String(rawAddress?.detail_address ?? ''),
    lat: isNaN(normalizedLat) ? DEFAULT_COORDINATES.lat : normalizedLat,
    lng: isNaN(normalizedLng) ? DEFAULT_COORDINATES.lng : normalizedLng,
  };

  // 원본 address 필드 보존 (address_kr, detail_address_kr 등) + 정규화 필드로 덮어쓰기
  const address = rawAddress
    ? { ...rawAddress, ...normalizedAddressFields } as StayAddress
    : normalizedAddressFields as StayAddress;

  // 원본 응답 전체 필드 보존 (display_info, note, installation_date 등) + 정규화 필드로 덮어쓰기
  return {
    ...data,
    id: String(data.id ?? ''),
    stay_name: String(data.stay_name ?? ''),
    stay_id: String(data.stay_id ?? ''),
    address_id: String(data.address_id ?? ''),
    created_date: String(data.created_date ?? ''),
    updated_date: String(data.updated_date ?? ''),
    address,
  } as StayInfo;
};

/**
 * 재시도 가능한 에러인지 판단합니다.
 * 네트워크 에러, 타임아웃, 5xx 에러는 재시도합니다.
 * 4xx (클라이언트 에러)는 재시도하지 않습니다.
 */
const isRetryableError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;
  const err = error as Record<string, unknown>;

  // axios 타임아웃
  if (err.code === 'ECONNABORTED') return true;

  // 네트워크 에러 (서버 응답 없음)
  if (!err.response) return true;

  // 서버 에러 (5xx)
  const response = err.response as Record<string, unknown>;
  const status = response.status as number;
  return status >= 500;
};

/**
 * 숙소 ID로 숙소 정보 조회
 * - timeout, retry(backoff), 응답 정규화 적용
 * - 필드 타입/null 차이는 normalizeStayInfo에서 조용히 처리
 * @param stayId 숙소 ID
 * @returns 정규화된 숙소 정보
 */
export const getStayInfo = async (stayId: string): Promise<StayInfo> => {
  const existingServiceUserId = getServiceUserId();

  // API 요청 URL 구성
  let apiUrl = `/api/stays/${stayId}`;
  if (existingServiceUserId) {
    apiUrl += `?serviceUserId=${existingServiceUserId}`;
  }

  let lastError: unknown = null;

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      if (attempt > 0) {
        // exponential backoff 대기
        const delay = BASE_BACKOFF * Math.pow(2, attempt - 1);
        await new Promise(resolve => setTimeout(resolve, delay));
        console.log(`[StayService] Retry attempt ${attempt}/${MAX_RETRIES} for stayId: ${stayId}`);
      }

      const response = await apiClient.get(apiUrl, {
        timeout: STAY_API_TIMEOUT,
      });

      // 구조적 최소 검사: object인지만 확인
      if (!isValidResponse(response.data)) {
        throw new Error('[StayService] API response is not a valid object');
      }

      // 모든 필드를 안전하게 정규화하여 반환 (throw 없음)
      return normalizeStayInfo(response.data);
    } catch (error) {
      lastError = error;

      // 재시도 불가능한 에러(4xx 등)면 즉시 중단
      if (!isRetryableError(error)) {
        break;
      }
    }
  }

  console.error('Failed to fetch stay info:', lastError);

  // 개발 환경에서 백엔드 서버가 없을 때 mock 데이터 반환
  if (process.env.NODE_ENV === 'development') {
    const mockStayInfo: StayInfo = {
      id: stayId,
      stay_name: 'Test Hotel',
      stay_id: stayId,
      address_id: 'mock-address-id',
      created_date: new Date().toISOString(),
      updated_date: new Date().toISOString(),
      address: {
        id: 'mock-address-id',
        address: '서울특별시 강남구 테헤란로 123',
        detail_address: '테스트 호텔 101호',
        lat: 37.5665,
        lng: 126.9780,
        address_kr: '서울특별시 강남구 테헤란로 123',
        detail_address_kr: '테스트 호텔 101호'
      }
    };

    // 실제 API 호출처럼 약간의 지연 추가
    await new Promise(resolve => setTimeout(resolve, 500));

    return mockStayInfo;
  }

  throw lastError;
}; 